import { PAGES } from "../../config/pages";
import { PAGE_VIEW_TYPES } from "../../config";
import axios from "axios";
import { ENDPOINT } from "@/config";
import * as queryString from "query-string";

// Default state
const defaultState = () => {
  return {
    // App
    appIsLoading: true,
    appType: null,
    appError: null,
    referrer: {
      user_source: null,
      voucher: {},
    },

    // Navigation
    currentPage: null,
    previousPages: [],
    pages: {},
  };
};

// State object
const state = defaultState();

// Getter functions
const getters = {
  getAppError(state) {
    return state.appError;
  },
  getReferrer(state) {
    return state.referrer;
  },
  getAppType(state) {
    return state.appType;
  },
  // Loading
  getAppIsLoading(state) {
    return state.appIsLoading;
  },
  getPageDataByName: (state) => (pageId) => {
    return state.pages[pageId];
  },
  // Get current page
  getCurrentPage(state) {
    return state.currentPage;
  },
  getPageField: (state) => (field) => {
    return state.pages[state.currentPage][field];
  },
};

// Actions
const actions = {
  // Init app
  setupApp({ commit }, data) {
    commit("SETUP_APP", data);
  },
  // Navigation
  setGoBackPage({ commit }) {
    commit("SET_GO_BACK_PAGE");
  },
  setCurrentPage({ commit }, newPage) {
    commit("SET_CURRENT_PAGE", newPage);
  },
  // Update page content
  updatePageContent({ commit }, data) {
    commit("SET_PAGE_CONTENT", data);
  },
  // Reset
  resetState({ commit }) {
    commit("RESET_STATE");
  },
  async getReferrerCode({ commit }, data) {
    commit("SET_REFERRER_CODE_IS_LOADING", true);

    const query = queryString.stringify({ code: data.referral_code });

    return new Promise((resolve, reject) => {
      axios
        .get(`${ENDPOINT.referral}?${query}`)
        .then((response) => {
          commit("SET_REFERRER_CODE_IS_LOADING", false);
          commit("SET_REFERRER_CODE", response.data);

          resolve(response.data);
        })
        .catch((error) => {
          if (error.response) {
            commit(
              "SET_ERROR",
              "This link appears to be broken. Please contact your referrer or ovrcome support: support@ovrcome.io"
            );
          }

          commit("SET_REFERRER_CODE_IS_LOADING", false);
          reject(error.response.data);
        });
    });
  },
};

// Mutations
const mutations = {
  SET_ERROR(state, err) {
    state.appError = err;
  },
  SET_REFERRER_CODE(state, data) {
    state.referrer = data;
  },
  SET_REFERRER_CODE_IS_LOADING(state, isLoading) {
    state.appIsLoading = isLoading;
  },
  SETUP_APP(state, data) {
    // Mobile paywall
    if (data["payment"] === PAGE_VIEW_TYPES.mobilePayment) {
      state.appType = PAGE_VIEW_TYPES.mobilePayment;
      state.pages = PAGES(PAGE_VIEW_TYPES.mobilePayment);
      state.currentPage = "mobile_payment__page";
    }
    // Therapist Client paywall
    else if (data["payment"] === PAGE_VIEW_TYPES.therapistClient) {
      state.appType = PAGE_VIEW_TYPES.therapistClient;
      state.pages = PAGES(PAGE_VIEW_TYPES.therapistClient);
      state.currentPage = "therapist_client_payment__page";
    }
    // Partner no onboarding with no paywall
    else if (
      data["payment"] === PAGE_VIEW_TYPES.partnerCheckout &&
      data["plan_id"] &&
      data["referral_code"]
    ) {
      state.appType = PAGE_VIEW_TYPES.partnerCheckout;
      state.pages = PAGES(PAGE_VIEW_TYPES.partnerCheckout);
      state.currentPage = "partner_payment__page";
    }
    // Partner onboarding with no payment
    else if (data["referral_code"]) {
      state.appType = PAGE_VIEW_TYPES.partnerOnboarding;
      state.pages = PAGES(PAGE_VIEW_TYPES.partnerOnboarding);
      state.currentPage = "partner_choose_problem_page";
    }
    // Partner onboarding with payment
    else if (data["referrer"]) {
      state.appType = PAGE_VIEW_TYPES.referral;
      state.pages = PAGES(PAGE_VIEW_TYPES.referral);
      if (data["referrer"] === "ease_up") {
        state.currentPage = "partnership_ease_up_page";
      } else if (data["referrer"] === "moe_vaping") {
        state.currentPage = "partnership_moe_vaping_page";
      } else if (data["referrer"] === "d2c_vaping") {
        state.currentPage = "d2c_vaping_page";
      } else if (data["referrer"] === "whakarongorau") {
        state.currentPage = "partnership_whakarongorau_page";
      } else if (data["referrer"] === "allianz") {
        state.currentPage = "partnership_allianz_page";
      } else {
        state.currentPage = "referrer_choose_problem_page";
      }
    }
    // Web onboarding
    else {
      state.appType = PAGE_VIEW_TYPES.web;
      state.pages = PAGES(PAGE_VIEW_TYPES.web);
      state.currentPage = "choose_path_page";
    }

    state.appIsLoading = false;
  },
  SET_PAGE_CONTENT(state, { pages, data }) {
    if (pages.length > 0) {
      pages.forEach((page) => {
        const pageData = state.pages[page.name].data[page.data_key];
        const result = page.action(data, pageData);

        if (result) {
          state.pages[page.name].data[page.data_key] = result;
        }
      });
    }
  },
  SET_CURRENT_PAGE(state, newPage) {
    state.previousPages = [...state.previousPages, state.currentPage];
    state.currentPage = newPage;
  },
  SET_GO_BACK_PAGE(state) {
    // Custom back
    if (state.pages[state.currentPage].data.custom_back) {
      state.currentPage = state.pages[state.currentPage].data.custom_back;
      state.previousPages.pop();
    }
    // Normal back
    else if (state.previousPages.length > 0) {
      state.currentPage = state.previousPages[state.previousPages.length - 1];
      state.previousPages.pop();
    }
  },
  RESET_STATE(state) {
    Object.assign(state, defaultState());
  },
};

export default {
  name: "AppModule",
  dynamic: true,
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
